import type { Presence } from "../../api/presence.api";

const time = (date: Date) =>
  date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * « En ligne », ou depuis quand la personne a été vue : à la minute dans l'heure, à l'heure
 * aujourd'hui et hier, au jour dans la semaine. Au-delà, rien n'est dit.
 */
export function presenceLabel(
  presence: Presence | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!presence) return null;
  if (presence.online) return "En ligne";
  if (!presence.lastSeenAt) return null;
  const seen = new Date(presence.lastSeenAt);
  if (Number.isNaN(seen.getTime())) return null;
  const minutes = Math.floor((now.getTime() - seen.getTime()) / 60_000);
  if (minutes < 1) return "Vu à l’instant";
  if (minutes < 60) return `Vu il y a ${minutes} min`;
  if (sameDay(seen, now)) return `Vu aujourd’hui à ${time(seen)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(seen, yesterday)) return `Vu hier à ${time(seen)}`;
  if (minutes < 7 * 24 * 60)
    return `Vu le ${seen.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}`;
  return null;
}

/** Pastille verte posée sur un avatar ; le texte « en ligne » est donné à part aux lecteurs d'écran. */
export function PresenceDot({ presence }: { presence?: Presence | null }) {
  if (!presence?.online) return null;
  return <span className="messaging-presence-dot" aria-hidden="true" />;
}

/** Mention discrète « en ligne » pour les lecteurs d'écran, à côté d'un nom. */
export function PresenceText({ presence }: { presence?: Presence | null }) {
  if (!presence?.online) return null;
  return <span className="messaging-sr-only"> (en ligne)</span>;
}

/** Libellé de présence sous le titre d'une conversation : « En ligne » en vert, sinon « Vu … ». */
export function PresenceLabel({ presence }: { presence?: Presence | null }) {
  const label = presenceLabel(presence);
  if (!label) return null;
  return (
    <span
      className={
        presence?.online
          ? "messaging-presence-label messaging-presence-label--online"
          : "messaging-presence-label"
      }
    >
      {presence?.online && <span className="messaging-presence-dot messaging-presence-dot--inline" aria-hidden="true" />}
      {label}
    </span>
  );
}
